import React, { useState, useEffect } from 'react';
import { Award, Download, Share2, Twitter, Facebook, MessageCircle, Send, DollarSign, Trophy } from 'lucide-react';
import { Button } from '../components/ui/button';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const AchievementsSection = () => {
  const [certificates, setCertificates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [selectedCert, setSelectedCert] = useState(null);

  useEffect(() => {
    fetchCertificates();
  }, []);

  const fetchCertificates = async () => {
    try {
      const response = await axios.get(`${API_URL}/certificates`);
      setCertificates(response.data);
    } catch (error) {
      console.error('Error fetching certificates:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (certificateId, certType) => {
    try {
      const response = await axios.get(
        `${API_URL}/certificates/${certificateId}/download`,
        { responseType: 'blob' }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const typeLabel = certType === 'payout' ? 'Payout' : 'Funded';
      link.setAttribute('download', `PropIndexa_${typeLabel}_Certificate_${certificateId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading certificate:', error);
      alert('Failed to download certificate');
    }
  };

  const handleShare = (cert) => {
    setSelectedCert(cert);
    setShareModalOpen(true);
  };

  const shareToSocial = (platform) => {
    let shareText = '';
    
    if (selectedCert.certificate_type === 'payout') {
      shareText = `💰 Just received a $${selectedCert.payout_amount?.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})} payout from PROP INDEXA! Trading with institutional capital pays off. #PropTrading #PayoutProof #PropIndexa`;
    } else {
      shareText = `🏆 I just got funded with a $${selectedCert.account_size?.toLocaleString()} account at PROP INDEXA! Trading ${selectedCert.trading_program || 'Synthetic Indices'} with institutional capital. #PropTrading #FundedTrader #PropIndexa`;
    }
    
    const shareUrl = window.location.origin;
    
    let url = '';
    switch (platform) {
      case 'twitter':
        url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`;
        break;
      case 'facebook':
        url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}&quote=${encodeURIComponent(shareText)}`;
        break;
      case 'whatsapp':
        url = `https://wa.me/?text=${encodeURIComponent(shareText + ' ' + shareUrl)}`;
        break;
      case 'telegram':
        url = `https://t.me/share/url?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(shareText)}`;
        break;
      default:
        return;
    }
    
    window.open(url, '_blank', 'width=600,height=400');
    setShareModalOpen(false);
  };

  // Separate certificates by type
  const fundedCerts = certificates.filter(c => c.certificate_type !== 'payout');
  const payoutCerts = certificates.filter(c => c.certificate_type === 'payout');

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-muted)' }}>
        Loading achievements...
      </div>
    );
  }

  const renderCertificateCard = (cert) => {
    const isPayout = cert.certificate_type === 'payout';
    
    return (
      <div key={cert.id} style={{
        background: 'linear-gradient(135deg, var(--bg-tertiary) 0%, var(--bg-secondary) 100%)',
        borderRadius: '16px',
        border: '1px solid var(--border-subtle)',
        overflow: 'hidden',
        transition: 'all 0.3s ease'
      }}>
        {/* Certificate Preview */}
        <div style={{
          height: '160px',
          background: isPayout 
            ? 'linear-gradient(135deg, #0d3320 0%, #1a2e1a 100%)'
            : 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
          borderBottom: '1px solid var(--border-subtle)'
        }}>
          <div style={{
            textAlign: 'center',
            padding: '20px'
          }}>
            <div style={{
              fontSize: '11px',
              color: isPayout ? '#22C55E' : 'var(--accent-gold)',
              textTransform: 'uppercase',
              letterSpacing: '2px',
              marginBottom: '8px'
            }}>
              {isPayout ? 'Payout Achievement' : 'Certificate of Achievement'}
            </div>
            <div style={{
              fontSize: '24px',
              fontWeight: 800,
              color: isPayout ? '#22C55E' : 'var(--accent-gold)',
              marginBottom: '4px'
            }}>
              {isPayout 
                ? `$${cert.payout_amount?.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}`
                : `$${cert.account_size?.toLocaleString()}`
              }
            </div>
            <div style={{
              fontSize: '12px',
              color: 'var(--text-muted)'
            }}>
              {isPayout ? 'Payout Approved' : 'Funded Trader'}
            </div>
          </div>
          <div style={{
            position: 'absolute',
            top: '12px',
            right: '12px',
            background: isPayout ? 'rgba(34, 197, 94, 0.2)' : 'rgba(212, 175, 55, 0.2)',
            padding: '4px 10px',
            borderRadius: '12px',
            fontSize: '10px',
            color: isPayout ? '#22C55E' : 'var(--accent-gold)',
            fontWeight: 600
          }}>
            ID: {cert.certificate_id}
          </div>
          {/* Icon badge */}
          <div style={{
            position: 'absolute',
            top: '12px',
            left: '12px',
            width: '32px',
            height: '32px',
            borderRadius: '50%',
            background: isPayout ? 'rgba(34, 197, 94, 0.2)' : 'rgba(212, 175, 55, 0.2)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            {isPayout 
              ? <DollarSign size={16} color="#22C55E" />
              : <Trophy size={16} color="var(--accent-gold)" />
            }
          </div>
        </div>

        {/* Certificate Details */}
        <div style={{ padding: '20px' }}>
          <div style={{ marginBottom: '16px' }}>
            <div style={{ fontSize: '16px', fontWeight: 600, color: 'var(--text-primary)', marginBottom: '4px' }}>
              {cert.trader_name}
            </div>
            <div style={{ fontSize: '13px', color: 'var(--text-muted)' }}>
              {isPayout 
                ? `Payout • ${new Date(cert.issued_at).toLocaleDateString()}`
                : `${cert.trading_program || 'Synthetic Indices'} • ${new Date(cert.issued_at).toLocaleDateString()}`
              }
            </div>
          </div>

          {/* Actions */}
          <div style={{ display: 'flex', gap: '8px' }}>
            <Button
              onClick={() => handleDownload(cert.certificate_id, cert.certificate_type)}
              size="sm"
              className={isPayout ? '' : 'btn-primary'}
              style={{ 
                flex: 1, 
                display: 'flex', 
                alignItems: 'center', 
                gap: '6px', 
                justifyContent: 'center',
                ...(isPayout && {
                  background: 'linear-gradient(135deg, #22C55E, #16A34A)',
                  border: 'none',
                  color: 'white'
                })
              }}
            >
              <Download size={14} />
              Download
            </Button>
            <Button
              onClick={() => handleShare(cert)}
              size="sm"
              variant="outline"
              style={{ 
                flex: 1, 
                display: 'flex', 
                alignItems: 'center', 
                gap: '6px', 
                justifyContent: 'center',
                border: '1px solid var(--border-primary)',
                background: 'transparent'
              }}
            >
              <Share2 size={14} />
              Share
            </Button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div>
      {/* Header */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: '24px'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div style={{
            width: '48px',
            height: '48px',
            borderRadius: '12px',
            background: 'linear-gradient(135deg, rgba(212, 175, 55, 0.2), rgba(212, 175, 55, 0.1))',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <Award size={24} color="var(--accent-gold)" />
          </div>
          <div>
            <h2 style={{ fontSize: '20px', fontWeight: 700, color: 'var(--text-primary)' }}>
              Achievements
            </h2>
            <p style={{ fontSize: '14px', color: 'var(--text-muted)' }}>
              Your trading certificates and achievements
            </p>
          </div>
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          {fundedCerts.length > 0 && (
            <div style={{
              background: 'var(--bg-tertiary)',
              padding: '8px 16px',
              borderRadius: '20px',
              fontSize: '14px',
              fontWeight: 600,
              color: 'var(--accent-gold)'
            }}>
              <Trophy size={14} style={{ display: 'inline', marginRight: '6px', verticalAlign: '-2px' }} />
              {fundedCerts.length} Funded
            </div>
          )}
          {payoutCerts.length > 0 && (
            <div style={{
              background: 'rgba(34, 197, 94, 0.1)',
              padding: '8px 16px',
              borderRadius: '20px',
              fontSize: '14px',
              fontWeight: 600,
              color: '#22C55E'
            }}>
              <DollarSign size={14} style={{ display: 'inline', marginRight: '4px', verticalAlign: '-2px' }} />
              {payoutCerts.length} Payout{payoutCerts.length !== 1 ? 's' : ''}
            </div>
          )}
        </div>
      </div>

      {/* Certificates Grid */}
      {certificates.length === 0 ? (
        <div style={{
          background: 'var(--bg-tertiary)',
          borderRadius: '16px',
          padding: '60px 40px',
          textAlign: 'center',
          border: '1px dashed var(--border-subtle)'
        }}>
          <Award size={48} color="var(--text-muted)" style={{ marginBottom: '16px', opacity: 0.5 }} />
          <h3 style={{ fontSize: '18px', fontWeight: 600, color: 'var(--text-primary)', marginBottom: '8px' }}>
            No Certificates Yet
          </h3>
          <p style={{ fontSize: '14px', color: 'var(--text-muted)', maxWidth: '400px', margin: '0 auto' }}>
            Certificates are awarded when your account is funded or when you receive an approved payout. 
            Keep trading and you'll earn your achievement badges!
          </p>
        </div>
      ) : (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
          gap: '20px'
        }}>
          {certificates.map(renderCertificateCard)}
        </div>
      )}

      {/* Share Modal */}
      {shareModalOpen && selectedCert && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.8)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999,
          padding: '20px'
        }} onClick={() => setShareModalOpen(false)}>
          <div style={{
            background: 'var(--bg-secondary)',
            borderRadius: '20px',
            padding: '32px',
            maxWidth: '400px',
            width: '100%',
            border: '1px solid var(--border-subtle)'
          }} onClick={e => e.stopPropagation()}>
            <h3 style={{ fontSize: '20px', fontWeight: 700, color: 'var(--text-primary)', marginBottom: '8px' }}>
              Share Your Achievement
            </h3>
            <p style={{ fontSize: '14px', color: 'var(--text-muted)', marginBottom: '24px' }}>
              {selectedCert.certificate_type === 'payout' 
                ? 'Let the world know you got paid!'
                : 'Let the world know you are a funded trader!'
              }
            </p>

            {/* Social Share Buttons */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              <button
                onClick={() => shareToSocial('twitter')}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  padding: '14px 20px',
                  background: '#1DA1F2',
                  border: 'none',
                  borderRadius: '12px',
                  color: 'white',
                  fontSize: '14px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  transition: 'transform 0.2s ease'
                }}
              >
                <Twitter size={18} />
                Twitter/X
              </button>
              <button
                onClick={() => shareToSocial('facebook')}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  padding: '14px 20px',
                  background: '#4267B2',
                  border: 'none',
                  borderRadius: '12px',
                  color: 'white',
                  fontSize: '14px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  transition: 'transform 0.2s ease'
                }}
              >
                <Facebook size={18} />
                Facebook
              </button>
              <button
                onClick={() => shareToSocial('whatsapp')}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  padding: '14px 20px',
                  background: '#25D366',
                  border: 'none',
                  borderRadius: '12px',
                  color: 'white',
                  fontSize: '14px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  transition: 'transform 0.2s ease'
                }}
              >
                <MessageCircle size={18} />
                WhatsApp
              </button>
              <button
                onClick={() => shareToSocial('telegram')}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  padding: '14px 20px',
                  background: '#0088cc',
                  border: 'none',
                  borderRadius: '12px',
                  color: 'white',
                  fontSize: '14px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  transition: 'transform 0.2s ease'
                }}
              >
                <Send size={18} />
                Telegram
              </button>
            </div>

            <button
              onClick={() => setShareModalOpen(false)}
              style={{
                width: '100%',
                marginTop: '16px',
                padding: '12px',
                background: 'transparent',
                border: '1px solid var(--border-primary)',
                borderRadius: '12px',
                color: 'var(--text-secondary)',
                fontSize: '14px',
                cursor: 'pointer'
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AchievementsSection;
